import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

// `LT` (locale time) and `ll` (locale long date) tokens are used by
// `formatDisplay`. Extending here keeps the helper standalone — callers
// don't need to know about plugin setup, and tests work without
// additional setup.
dayjs.extend(localizedFormat);

const DEFAULT_TIME_FALLBACK = '09:00';

/** Locale-aware dayjs factory. Compatible with `useLocalizedDayjs()`
 *  from `@/languages/dayjs`. */
type LocalizedDayjs = (date?: dayjs.ConfigType) => dayjs.Dayjs;

/** Format an ISO datetime for display.
 *
 *  Returns `null` when the input is missing or unparseable so the
 *  consumer can fall back to its own placeholder (rather than rendering
 *  raw garbage from a misshapen server payload).
 *
 *  Pass a locale-aware dayjs factory (e.g. `useLocalizedDayjs()`) so
 *  month abbreviations and time format (12h vs 24h) are localized:
 *  - EN → "May 8, 2026 · 2:00 PM"
 *  - FR → "8 mai 2026 · 14:00"
 *
 *  The format relies on dayjs `localizedFormat` tokens (`ll` + `LT`),
 *  which is why this helper extends the plugin at module load. */
export const formatDisplay = (
  iso: string,
  dayjsFn: LocalizedDayjs,
  separator = '·',
): string | null => {
  if (!iso) {
    return null;
  }
  const d = dayjsFn(iso);
  if (!d.isValid()) {
    return null;
  }
  return d.format(`ll [${separator}] LT`);
};

/** Combine a calendar date and an `HH:mm` time string into an ISO
 *  datetime. Hours/minutes are clamped to `[0,23]` / `[0,59]` and
 *  default to 0 if the time string is malformed.
 *
 *  DST caveat: on spring-forward, the local hour that doesn't exist
 *  (e.g. 2:30 AM where the clock jumps to 3:00 AM) is browser-defined
 *  behavior via `Date.prototype.setHours`. In practice the time picker
 *  doesn't surface non-existent hours, so this isn't reachable from the
 *  current consumers — noted for future callers that might. */
export const toIsoFromDateAndTime = (date: Date, hhmm: string): string => {
  const [hStr, mStr] = hhmm.split(':');
  const h = Math.min(23, Math.max(0, Number.parseInt(hStr ?? '0', 10) || 0));
  const m = Math.min(59, Math.max(0, Number.parseInt(mStr ?? '0', 10) || 0));
  const next = new Date(date);
  next.setHours(h, m, 0, 0);
  return next.toISOString();
};

/** Extract the `HH:mm` portion from an ISO datetime. Falls back to
 *  `fallback` (default `'09:00'`) for invalid inputs so the time picker
 *  has a usable default — consumers can override per surface. */
export const extractTime = (iso: string, fallback = DEFAULT_TIME_FALLBACK): string => {
  if (!iso) {
    return fallback;
  }
  const d = dayjs(iso);
  if (!d.isValid()) {
    return fallback;
  }
  return d.format('HH:mm');
};

/** Extract the date portion of an ISO datetime as a JS `Date`.
 *  Returns `undefined` if the input is not parseable. */
export const extractDate = (iso: string): Date | undefined => {
  if (!iso) {
    return undefined;
  }
  const d = dayjs(iso);
  return d.isValid() ? d.toDate() : undefined;
};

/** Format a `Date` as a `YYYY-MM-DD` string in the date's local
 *  Y/M/D — does NOT timezone-shift via UTC.
 *
 *  Use this when persisting a date-only value picked via a calendar:
 *  `Calendar` returns a local `Date`, and we want to round-trip the
 *  exact day the user clicked, not the UTC equivalent. */
export const toIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
